export type ExpectedCloseV2DealForecast = {
  dealId: number;
  wonProbabilityByMonth: Array<[month: string, probability: number]>;
};

export type ExpectedCloseV2TerminalBuyerOutcome = {
  confidence: number;
  dealId: number;
  detectedAt: string;
  evidence: string;
  sourceMessageId: string | null;
};

export type ExpectedCloseV2CompanyForecast = {
  companyId: number;
  openDeals: number;
  supportedDeals: number;
  matchedSupportedDeals: number;
  dealForecasts: ExpectedCloseV2DealForecast[];
  terminalBuyerOutcomeDeals?: ExpectedCloseV2TerminalBuyerOutcome[];
};

export type ExpectedCloseV2LocalForecastArtifact = {
  schemaVersion: "expected-close-v2-local-company-forecast-v3";
  asOf: string;
  generatedAt: string;
  horizonWeeks: 53;
  thresholdAppliedToCurve: false;
  company: ExpectedCloseV2CompanyForecast;
};

export type ExpectedCloseV2CanonicalForecastRow = {
  month: string;
  total_minus_risk: number;
  won?: number;
  is_context?: boolean;
};

export type ExpectedCloseV2DealValue = {
  adjustedCloseDate?: Date | string | null;
  forecast_contribution?: number | null;
  id: number;
  value: number;
};

export type ExpectedCloseV2ContributionDeal = ExpectedCloseV2DealValue & {
  forecast_contribution?: number | null;
};

function finiteProbability(value: number) {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
}

function monthKey(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getTerminalBuyerOutcomeDealIds(
  companyForecast: ExpectedCloseV2CompanyForecast,
) {
  return new Set(
    (companyForecast.terminalBuyerOutcomeDeals ?? []).map(outcome => Number(outcome.dealId)),
  );
}

/**
 * Builds the approved Forecast directly from Deal-level v2 probability mass.
 * Existing Won revenue remains immutable; only unsupported Deals retain their
 * reviewed v1 contribution.
 */
export function applyExpectedCloseV2Forecast(
  canonicalRows: readonly ExpectedCloseV2CanonicalForecastRow[],
  companyForecast: ExpectedCloseV2CompanyForecast | undefined,
  selectedDeals: readonly ExpectedCloseV2DealValue[],
  accumulated: boolean,
  selectedPeriodStart?: string | null,
) {
  if (!companyForecast) {
    return canonicalRows.map(row => ({ ...row, expected_close_v2: null }));
  }

  const forecastByDealId = new Map(
    companyForecast.dealForecasts.map(forecast => [forecast.dealId, forecast]),
  );
  const terminalBuyerOutcomeDealIds = getTerminalBuyerOutcomeDealIds(companyForecast);
  const openForecastByMonth = new Map<string, number>();
  for (const deal of selectedDeals) {
    const value = Number(deal.value);
    if (!Number.isFinite(value) || value <= 0) continue;
    if (terminalBuyerOutcomeDealIds.has(Number(deal.id))) continue;
    const forecast = forecastByDealId.get(Number(deal.id));
    if (!forecast) {
      const fallbackMonth = monthKey(deal.adjustedCloseDate);
      const fallbackValue = Number(deal.forecast_contribution ?? 0);
      if (fallbackMonth && Number.isFinite(fallbackValue) && fallbackValue > 0) {
        openForecastByMonth.set(
          fallbackMonth,
          (openForecastByMonth.get(fallbackMonth) ?? 0) + fallbackValue,
        );
      }
      continue;
    }
    for (const monthForecast of forecast.wonProbabilityByMonth) {
      const [month, probability] = monthForecast;
      const expectedValue = value * finiteProbability(probability);
      openForecastByMonth.set(
        month,
        (openForecastByMonth.get(month) ?? 0) + expectedValue,
      );
    }
  }

  let accumulatedOpenForecast = 0;
  let accumulatedWon = 0;
  return canonicalRows.map(row => {
    const monthlyOpenForecast = openForecastByMonth.get(row.month) ?? 0;
    if (!accumulated) {
      return {
        ...row,
        expected_close_v2: Math.max(0, Number(row.won ?? 0) + monthlyOpenForecast),
      };
    }

    if (row.is_context || (selectedPeriodStart && row.month === selectedPeriodStart)) {
      accumulatedOpenForecast = 0;
      accumulatedWon = 0;
    }
    accumulatedOpenForecast += monthlyOpenForecast;
    accumulatedWon += Number(row.won ?? 0);
    return {
      ...row,
      expected_close_v2: Math.max(0, accumulatedWon + accumulatedOpenForecast),
    };
  });
}

export function applyExpectedCloseV2DealContributions<Deal extends ExpectedCloseV2ContributionDeal>(
  deals: readonly Deal[],
  companyForecast: ExpectedCloseV2CompanyForecast | undefined,
  selectedMonths: ReadonlySet<string>,
) {
  if (!companyForecast) return deals;
  const forecastByDealId = new Map(
    companyForecast.dealForecasts.map(forecast => [forecast.dealId, forecast]),
  );
  const terminalBuyerOutcomeDealIds = getTerminalBuyerOutcomeDealIds(companyForecast);

  return deals.map(deal => {
    if (terminalBuyerOutcomeDealIds.has(Number(deal.id))) {
      return { ...deal, forecast_contribution: 0 };
    }
    const forecast = forecastByDealId.get(Number(deal.id));
    if (!forecast) return deal;
    const value = Number(deal.value);
    if (!Number.isFinite(value) || value <= 0) return deal;
    const wonProbability = forecast.wonProbabilityByMonth.reduce(
      (sum, [month, probability]) => selectedMonths.has(month)
        ? sum + finiteProbability(probability)
        : sum,
      0,
    );
    return {
      ...deal,
      forecast_contribution: Math.max(0, value * wonProbability),
    };
  }).filter(deal => {
    const forecast = forecastByDealId.get(Number(deal.id));
    if (forecast) return true;
    const fallbackMonth = monthKey(deal.adjustedCloseDate);
    return fallbackMonth !== null && selectedMonths.has(fallbackMonth);
  });
}
